import React, { useState, useRef, useEffect } from "react";
import { contactData, socialLinks } from "@/data/mock";
import { Mail, Instagram, Send, CheckCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import axios from "axios";

const BACKEND_URL = window.__CONFIG__?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const useInView = (threshold = 0.1) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, visible];
};

const ServicesSection = () => {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "" });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [ctaRef, ctaVisible] = useInView(0.2);
  const [contactRef, contactVisible] = useInView(0.1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/contact`, formData);
      setFormSubmitted(true);
      setFormData({ name: "", email: "", phone: "", message: "" });
      setTimeout(() => setFormSubmitted(false), 3000);
    } catch (err) {
      console.error("Error sending message:", err);
      alert("Failed to send message. Please try again.");
    }
  };

  return (
    <>
      {/* Brand Collaboration CTA */}
      <section ref={ctaRef} className="bg-[#1A1A1A] py-24 lg:py-28 border-t border-white/5">
        <div
          className={`max-w-3xl mx-auto px-6 lg:px-12 text-center transition-all duration-700 ${
            ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <p className="font-inter text-xs tracking-[0.25em] uppercase text-[#C4956A] mb-6">
            Brand Collaborations
          </p>
          <h2 className="font-bebas text-3xl sm:text-4xl lg:text-5xl text-white tracking-[0.08em] mb-5 leading-tight">
            BRING YOUR BRAND TO LIFE<br />THROUGH CINEMATIC STORYTELLING
          </h2>
          <p className="font-inter text-sm text-white/35 max-w-lg mx-auto leading-relaxed">
            Crafting visual experiences that capture attention, tell meaningful stories, and leave a lasting impression.
          </p>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" ref={contactRef} className="bg-[#F5F0E8] py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            <div
              className={`transition-all duration-700 ${
                contactVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
            >
              <p className="font-inter text-xs tracking-[0.25em] uppercase text-[#8B7355] mb-4">
                Get in Touch
              </p>
              <h2 className="font-bebas text-5xl lg:text-6xl text-[#1A1A1A] tracking-[0.08em] mb-8 leading-tight">
                Have something in mind?<br />Let’s create.
              </h2>
              <div className="space-y-4 mb-8">
                <a
                  href={`mailto:${contactData.email}`}
                  className="flex items-center gap-3 font-inter text-sm text-[#6B6B6B] hover:text-[#8B7355] transition-colors duration-300"
                >
                  <Mail size={16} />
                  {contactData.email}
                </a>
                <a
                  href={socialLinks.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 font-inter text-sm text-[#6B6B6B] hover:text-[#8B7355] transition-colors duration-300"
                >
                  <Instagram size={16} />
                  {contactData.instagram}
                </a>
              </div>
            </div>
            <div
              className={`transition-all duration-700 delay-200 ${
                contactVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
            >
              <form onSubmit={handleSubmit} className="space-y-5">
                <Input
                  type="text"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="px-5 py-4 h-auto bg-[#1A1A1A] border-0 rounded-none font-inter text-sm text-white placeholder:text-white/30 focus-visible:ring-1 focus-visible:ring-[#C4956A]/40"
                  required
                />
                <Input
                  type="email"
                  placeholder="Your Email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="px-5 py-4 h-auto bg-[#1A1A1A] border-0 rounded-none font-inter text-sm text-white placeholder:text-white/30 focus-visible:ring-1 focus-visible:ring-[#C4956A]/40"
                  required
                />
                <Input
                  type="tel"
                  placeholder="Your Phone Number"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="px-5 py-4 h-auto bg-[#1A1A1A] border-0 rounded-none font-inter text-sm text-white placeholder:text-white/30 focus-visible:ring-1 focus-visible:ring-[#C4956A]/40"
                />
                <Textarea
                  placeholder="Tell me about your project..."
                  rows={5}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="px-5 py-4 bg-[#1A1A1A] border-0 rounded-none font-inter text-sm text-white placeholder:text-white/30 focus-visible:ring-1 focus-visible:ring-[#C4956A]/40 resize-none"
                  required
                />
                <Button
                  type="submit"
                  disabled={formSubmitted}
                  className={`px-8 py-4 h-auto font-inter text-xs uppercase tracking-[0.15em] rounded-none border transition-all duration-300 ${
                    formSubmitted
                      ? "bg-emerald-700 border-emerald-700 text-white"
                      : "bg-[#1A1A1A] border-[#1A1A1A] text-white hover:bg-transparent hover:text-[#1A1A1A]"
                  }`}
                >
                  {formSubmitted ? (
                    <><CheckCircle size={14} className="mr-2" /> Message Sent!</>
                  ) : (
                    <><Send size={14} className="mr-2" /> Send Message</>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ServicesSection;
